import { useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { logError } from '@/lib/sentry';

interface ErrorEvent {
  message: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  error?: Error;
}

export const GlobalErrorHandler = () => {
  const { toast } = useToast();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event);
      
      // Show user-friendly error toast
      toast({
        variant: "destructive",
        title: "An error occurred",
        description: "Something went wrong. Please try again or contact support if the issue persists.",
      });

      // Send to Sentry
      logError(event.error || new Error(event.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      toast({
        variant: "destructive",
        title: "Network Error",
        description: "A network request failed. Please check your connection and try again.",
      });

      // Send to Sentry
      logError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
    };

    // Listen for global errors
    window.addEventListener('error', handleError as any);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError as any);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [toast]);

  return null;
};